import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  ExecuteCancelDialog,
  type ExecuteCancelDialogProps,
} from '~/components/elements/ExecuteCancelDialog'
import { useLogEvent } from '~/hooks/useLogEvent'

import { dialogInfoAtom } from '../atoms'
import { useInvalidateArchivedDocumentImagesInfiniteQuery } from '../hooks/useArchivedDocumentImagesInfiniteQuery'
import { useUpdateDocumentImageStatusMutation } from '../hooks/useUpdateDocumentImageStatusMutation'

export const DeleteDocumentImageDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'delete') {
    throw new Error()
  }
  const { documentImage } = dialogInfo

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const invalidateArchivedDocumentImagesInfiniteQuery =
    useInvalidateArchivedDocumentImagesInfiniteQuery()
  const updateDocumentImageStatusMutation =
    useUpdateDocumentImageStatusMutation({
      onSuccess: () => {
        invalidateArchivedDocumentImagesInfiniteQuery()
        resetDialogInfo()
      },
    })
  const logEvent = useLogEvent()
  const handleExecute = React.useCallback<
    EH<ExecuteCancelDialogProps, 'onExecute'>
  >(() => {
    updateDocumentImageStatusMutation.mutate({
      documentImageId: documentImage.id,
      newStatus: 'removed',
    })
    logEvent('Delete saved table and graph completely')
  }, [documentImage.id, logEvent, updateDocumentImageStatusMutation])

  return (
    <ExecuteCancelDialog
      executing={updateDocumentImageStatusMutation.isPending}
      mode="delete"
      title={t('permanently_delete_tablechart')}
      onCancel={resetDialogInfo}
      onExecute={handleExecute}
    >
      {t('delete_completely_confirmation', { target: documentImage.title })}
    </ExecuteCancelDialog>
  )
}
